import React, { useRef } from 'react';
import { Input, Button, Tooltip } from 'antd';
import { MdFormatBold, MdFormatListBulleted } from 'react-icons/md';
import styles from './styles.module.css';

const { TextArea } = Input;

const BULLET_RE = /^\s*[-*•]\s+/;

/**
 * Textarea with a small toolbar for the guide description.
 * Stores lightweight markup understood by the app and the preview:
 *   **text**  → bold
 *   - text    → bullet point (one per line)
 * Works as an antd Form.Item child (receives value / onChange).
 */
const GuideDescriptionEditor = ({ value = '', onChange, maxLength, placeholder, rows = 6 }) => {
    const ref = useRef(null);

    const getTextarea = () => ref.current?.resizableTextArea?.textArea;

    // Apply new text and restore the selection after React re-renders.
    const commit = (text, selStart, selEnd) => {
        if (maxLength && text.length > maxLength) return;
        onChange?.(text);
        requestAnimationFrame(() => {
            const ta = getTextarea();
            if (!ta) return;
            ta.focus();
            ta.setSelectionRange(selStart, selEnd);
        });
    };

    const toggleBold = () => {
        const ta = getTextarea();
        if (!ta) return;
        const { selectionStart: s, selectionEnd: e } = ta;
        const before = value.slice(0, s);
        const selected = value.slice(s, e);
        const after = value.slice(e);

        // Already wrapped → unwrap
        if (before.endsWith('**') && after.startsWith('**')) {
            commit(before.slice(0, -2) + selected + after.slice(2), s - 2, e - 2);
            return;
        }
        if (selected.startsWith('**') && selected.endsWith('**') && selected.length >= 4) {
            const inner = selected.slice(2, -2);
            commit(before + inner + after, s, s + inner.length);
            return;
        }
        // Nothing selected → insert placeholder and select it
        const inner = selected || 'bold text';
        commit(`${before}**${inner}**${after}`, s + 2, s + 2 + inner.length);
    };

    const toggleBullet = () => {
        const ta = getTextarea();
        if (!ta) return;
        const { selectionStart: s, selectionEnd: e } = ta;

        // Expand selection to whole lines
        const lineStart = value.lastIndexOf('\n', s - 1) + 1;
        let lineEnd = value.indexOf('\n', e);
        if (lineEnd === -1) lineEnd = value.length;

        const lines = value.slice(lineStart, lineEnd).split('\n');
        const allBulleted = lines.every((l) => !l.trim() || BULLET_RE.test(l));
        const updated = lines.map((l) => {
            if (!l.trim()) return l;
            return allBulleted ? l.replace(BULLET_RE, '') : (BULLET_RE.test(l) ? l : `- ${l}`);
        }).join('\n');

        const text = value.slice(0, lineStart) + updated + value.slice(lineEnd);
        commit(text, lineStart + updated.length, lineStart + updated.length);
    };

    const handleKeyDown = (ev) => {
        const mod = ev.ctrlKey || ev.metaKey;
        if (mod && ev.key.toLowerCase() === 'b') {
            ev.preventDefault();
            toggleBold();
            return;
        }
        // Enter on a bullet line continues the list; Enter on an empty bullet ends it
        if (ev.key === 'Enter' && !ev.shiftKey) {
            const ta = getTextarea();
            const { selectionStart: s, selectionEnd: e } = ta;
            if (s !== e) return;
            const lineStart = value.lastIndexOf('\n', s - 1) + 1;
            const line = value.slice(lineStart, s);
            if (!BULLET_RE.test(line)) return;
            ev.preventDefault();
            if (!line.replace(BULLET_RE, '').trim()) {
                commit(value.slice(0, lineStart) + value.slice(s), lineStart, lineStart);
            } else {
                const insert = '\n- ';
                commit(value.slice(0, s) + insert + value.slice(s), s + insert.length, s + insert.length);
            }
        }
    };

    return (
        <div className={styles['editor']}>
            <div className={styles['editor-toolbar']}>
                <Tooltip title="Bold (Ctrl+B)">
                    <Button
                        size="small"
                        type="text"
                        icon={<MdFormatBold size={18} />}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={toggleBold}
                    />
                </Tooltip>
                <Tooltip title="Bullet list">
                    <Button
                        size="small"
                        type="text"
                        icon={<MdFormatListBulleted size={18} />}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={toggleBullet}
                    />
                </Tooltip>
            </div>
            <TextArea
                ref={ref}
                value={value}
                onChange={(ev) => onChange?.(ev.target.value)}
                onKeyDown={handleKeyDown}
                rows={rows}
                maxLength={maxLength}
                showCount={!!maxLength}
                placeholder={placeholder}
                bordered={false}
                className={styles['editor-textarea']}
            />
        </div>
    );
};

export default GuideDescriptionEditor;
